import { useState } from 'react';
import t from 'react-translate';

import { css } from '@emotion/react';
import { gray6 } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { textMediumLineHeight } from 'styles/global_defaults/fonts';

import { NvTooltip } from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import ParticipantsQuestionsPopover from './participants-questions-popover';

const styles = css`
  display: grid;
  align-items: center;
  column-gap: ${standardSpacing}px;
  grid-auto-rows: auto;
  grid-template-columns: ${standardSpacing}px auto ${tripleSpacing}px;
  border-bottom: 1px solid ${gray6};
  .switch {
    width: ${doubleSpacing}px;
    height: ${standardSpacing}px;
    transform: scale(1.5);
    justify-self: center;
  }
  .question-text {
    line-height: ${textMediumLineHeight}px !important;
  }
`;

const overlayStyles = css`
  max-width: 280px;
  .confirmation {
    max-width: 100%;
    .button-container {
      justify-content: center;
    }
  }
`;

type SelectableQuestionRowProps = {
  isHidden?: boolean;
  isSelected: boolean;
  questionId: number;
  questionText: string;
  onChange: (questionId: number, isSelected: boolean, isAttachedToRule: boolean, attachedRules: { id: number }[]) => void;
  isAttachedToRule: boolean;
  attachedRules?: { id: number }[];
  dataQa?: string;
  dataQaId?: string;
  menteeQuestion?: string;
  mentorQuestion?: string;
};

const SelectableQuestionRow = ({
  isHidden = false,
  isSelected,
  questionId,
  questionText,
  onChange,
  isAttachedToRule,
  attachedRules,
  dataQa,
  dataQaId,
  menteeQuestion,
  mentorQuestion,
}: SelectableQuestionRowProps) => {
  const [isChecked, setIsChecked] = useState(isAttachedToRule || isSelected);
  const [isSwitchingRequired, setIsSwitchingRequired] = useState(false);
  const flyoutTranslationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .CONFIGURATION.ASSIGNED.MATCHING_RULES.FLYOUT_MODAL;
  const tooltip = isHidden ? flyoutTranslationBase.TOOLTIP.HIDDEN() : flyoutTranslationBase.TOOLTIP.SHOWN();

  const handleRequiredChange = (newState) => {
    if (!newState) setIsSwitchingRequired(true);
    else {
      setIsChecked(newState);
      onChange(questionId, newState, isAttachedToRule, attachedRules);
    }
  };

  const handleConfirm = () => {
    setIsSwitchingRequired(false);
    setIsChecked(false);
    onChange(questionId, false, isAttachedToRule, attachedRules);
  };

  return (
    <div css={styles}>
      <NvTooltip placement='top' preventOverflow={false} text={tooltip}>
        <NvIcon size='small' icon={isHidden ? 'hide' : 'view'} />
      </NvTooltip>
      <div className='text-large-body semi-bold d-flex flex-row align-items-center question-text py-2'>
        {menteeQuestion && mentorQuestion && (
          <ParticipantsQuestionsPopover
            menteeQuestion={menteeQuestion}
            mentorQuestion={mentorQuestion}
          />
        )}
        {questionText}
      </div>
      <NvPopover
        enabled
        show={isSwitchingRequired}
        overlayStyles={overlayStyles}
        content={(
          <NvConfirmationPopover
            onCancel={() => setIsSwitchingRequired(false)}
            onConfirm={handleConfirm}
            header={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.TITLE()}
            message={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.DESCRIPTION()}
            cancelBtnText={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.CANCEL()}
            confirmBtnText={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.OK()}
          />
        )}
        placement='left'
        rootClose
        onHide={() => setIsSwitchingRequired(false)}
        className='d-inline-flex mw-100'
        preventOverflow
      >
        <NvSwitch
          checked={isChecked}
          className='switch align-self-center'
          onChange={handleRequiredChange}
          dataQa={dataQa}
          dataQaId={dataQaId}
          ariaLabel={
            isChecked
              ? flyoutTranslationBase.ARIA_LABEL.DISABLE_RULE(questionText)
              : flyoutTranslationBase.ARIA_LABEL.ENABLE_RULE(questionText)
          }
        />
      </NvPopover>
    </div>
  );
};

export default SelectableQuestionRow;
